import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import ModuleEditorBase from './ModuleEditorBase';
import DropZone from '../../../pages/courses/wizard/shared/DropZone';
import { uploadFile } from '../../../services/uploads';
import { isEmpty, PENDING_PLACEHOLDER } from './contentPlaceholder';

interface PDFUploadEditorProps {
  module: {
    id: string;
    title: string;
    type: string;
    details: {
      file_url?: string;
      filename?: string;
      size_bytes?: number;
      page_count?: number;
    };
  };
  sectionId: string;
  courseId: string;
  onUpdate: (moduleId: string, updates: any) => Promise<void>;
  onDelete: (moduleId: string) => Promise<void>;
}

const MAX_FILE_SIZE_MB = 50;

function PDFFileCard({
  filename,
  sizeBytes,
  onRemove,
}: {
  filename: string;
  sizeBytes: number;
  onRemove: () => void;
}) {
  return (
    <div className="border border-border-primary rounded-2xl p-4 bg-surface-primary">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <svg className="h-10 w-10 text-red-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" />
          </svg>
          <div className="min-w-0">
            <p className="text-sm font-medium text-text-primary truncate">
              {filename}
            </p>
            <p className="text-xs text-text-secondary">
              {(sizeBytes / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-sm text-error-600 hover:text-error-700 dark:text-error-400 dark:hover:text-error-300 shrink-0"
        >
          Remove
        </button>
      </div>
    </div>
  );
}

export default function PDFUploadEditor({
  module,
  sectionId,
  onUpdate,
  onDelete,
}: PDFUploadEditorProps) {
  const [details, setDetails] = useState(module.details);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    setDetails(module.details);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.id, module.details.file_url, module.details.filename]);

  const handleFilesSelected = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploadResult = await uploadFile(
        {
          file,
          resource_type: 'module_file' as const,
          resource_id: module.id,
          section_id: sectionId,
        },
        {
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          },
        }
      );

      const nextDetails = {
        file_url: uploadResult.key,
        filename: file.name,
        size_bytes: file.size,
        page_count: 0,
      };
      setDetails(nextDetails);
      await onUpdate(module.id, { details: nextDetails });
    } catch (error) {
      console.error('Failed to upload PDF:', error);
      toast.error('Failed to upload PDF');
    } finally {
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 300);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove this PDF?')) return;
    const nextDetails = { file_url: PENDING_PLACEHOLDER, filename: '', size_bytes: 0 };
    setDetails(nextDetails);
    await onUpdate(module.id, { details: nextDetails });
  };

  return (
    <ModuleEditorBase
      moduleType={module.type}
      moduleTitle={module.title}
      onTitleChange={async (title) => await onUpdate(module.id, { title })}
      onDelete={async () => await onDelete(module.id)}
      isSaving={uploading}
    >
      {isEmpty(details.file_url) ? (
        <DropZone
          onFilesSelected={handleFilesSelected}
          accept="application/pdf"
          maxSizeMB={MAX_FILE_SIZE_MB}
          variant="file"
        />
      ) : (
        <PDFFileCard
          filename={details.filename || ''}
          sizeBytes={details.size_bytes ?? 0}
          onRemove={handleRemove}
        />
      )}

      {uploading && (
        <div className="mt-2">
          <div className="w-full bg-grey-200 dark:bg-grey-700 rounded-full h-2">
            <div
              className="bg-primary-600 dark:bg-primary-350 h-2 rounded-full transition-[width] duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-text-secondary text-center">
            Uploading... {uploadProgress}%
          </p>
        </div>
      )}
    </ModuleEditorBase>
  );
}
